import { useQuery } from '@apollo/client';
import { Box, Grid, ThemeProvider, useTheme } from '@mui/material';
import type Konva from 'konva';
import { useCallback, useMemo, useRef } from 'react';
import { Group, Layer, Line, Rect, Stage } from 'react-konva';
import { useParams } from 'react-router-dom';
import { FolderContextProvider } from '~/contexts';
import { StudioProvider, useStudio } from '../context';
import { DesignEditDocument } from './DesignEdit.generated';
import {
  StudioActionBar,
  StudioBaseItem,
  StudioGroupTransformer,
  StudioItemTransformer,
  StudioLayers,
  StudioRulers,
  StudioSelectionBox,
  StudioSidebar,
  StudioToolbar,
  StudioZoomControls,
} from './components';
import { useClick, useFonts, useKeys, useLoadDesign, useResize, useSnap } from './lib';

export const StudioEdit = () => {
  useFonts();

  // react-konva runs its own reconciler, so context does not cross into the <Stage>. Read the
  // theme out here and re-provide it below, the same way the studio context is bridged.
  const theme = useTheme();

  const studio = useStudio();
  const params = useParams<{ designId: string; networkId: string }>();

  const layerRef = useRef<Konva.Layer>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery(DesignEditDocument, {
    variables: { id: Number(params.designId), networkId: Number(params.networkId) },
  });

  const design = useMemo(() => data?.network?.design, [data]);
  const contentFolderTree = useMemo(() => data?.network?.contentFolderTree, [data]);
  const folders = useMemo(() => data?.network?.contentFolders, [data]);

  const { onContainerClick, onStageClick } = useClick(layerRef.current);
  const { height, width, scale, offsetX, offsetY, canScrollX, canScrollY } = useResize(
    design,
    scrollRef,
  );
  const { lines, onDragEnd, onDragMove } = useSnap();

  const stageRef = useCallback(
    (stage: Konva.Stage | null) => {
      if (!stage || stage === studio.stage) return;
      studio.setStage(stage);
    },
    [studio],
  );

  useKeys();
  useLoadDesign(design);

  if (!design || !studio.fonts || !contentFolderTree) return null;

  return (
    <FolderContextProvider folderTree={contentFolderTree} folders={folders}>
      <StudioToolbar design={design} />
      <Grid
        container
        sx={{
          height: 'calc(100% - 64px)',
          backgroundColor: 'studio.canvas',
        }}
      >
        <Grid item sx={{ width: '350px', height: '100%' }}>
          <StudioSidebar />
        </Grid>
        <Grid
          item
          xs
          sx={{ minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <Box
            onClick={onContainerClick}
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              height: '100%',
              position: 'relative',
              '& *': {
                userSelect: 'none',
              },
              // The stage container, focusable for the editor's keyboard shortcuts.
              '& [tabindex]:focus-visible': theme.mixins.focusRingInset,
            }}
          >
            <StudioRulers
              canScrollX={canScrollX}
              canScrollY={canScrollY}
              designHeight={design.height}
              designWidth={design.width}
              offsetX={offsetX}
              offsetY={offsetY}
              scale={scale}
              scrollRef={scrollRef}
              stageHeight={height}
              stageWidth={width}
            >
              <Stage
                width={width}
                height={height}
                scaleX={scale}
                scaleY={scale}
                onClick={onStageClick}
                ref={stageRef}
              >
                <Layer onDragEnd={onDragEnd} onDragMove={onDragMove} ref={layerRef}>
                  <ThemeProvider theme={theme}>
                    <StudioProvider {...studio}>
                      {/* Offset group to center design in stage */}
                      <Group id="design-offset" x={offsetX} y={offsetY}>
                        {/* Design frame background */}
                        <Rect
                          width={design.width}
                          height={design.height}
                          fill="white"
                          shadowColor="rgba(0,0,0,0.15)"
                          shadowBlur={20}
                          shadowOffsetY={4}
                          listening={false}
                        />

                        {/* Clipped content area - items clipped to design bounds */}
                        <Group
                          clipFunc={(ctx: CanvasRenderingContext2D) => {
                            ctx.rect(0, 0, design.width, design.height);
                          }}
                        >
                          {studio.items.map((item) => (
                            <StudioBaseItem
                              item={item}
                              key={item.id}
                              previewText={studio.previewTextFor(item)}
                            />
                          ))}
                        </Group>

                        {/* Selection box and transformer outside clip so they work beyond design edges */}
                        <StudioSelectionBox offsetX={offsetX} offsetY={offsetY} />
                        {studio.items.map((item) => {
                          const isSelected = studio.selectedItems.some((s) => s.id === item.id);
                          return (
                            <StudioItemTransformer
                              item={item}
                              isSelected={isSelected}
                              isMultiSelected={isSelected && studio.selectedItems.length > 1}
                              key={item.id}
                            />
                          );
                        })}
                        <StudioGroupTransformer />
                      </Group>

                      {/* Guide lines outside offset group - uses stage coordinates */}
                      {lines.map((line, i) => (
                        <Line key={i} {...line} />
                      ))}

                      {/* Action bar outside offset group - uses absolute coordinates */}
                      <StudioActionBar />
                    </StudioProvider>
                  </ThemeProvider>
                </Layer>
              </Stage>
            </StudioRulers>
          </Box>
          <StudioZoomControls />
        </Grid>
        <Grid item sx={{ width: '200px', height: '100%' }}>
          <StudioLayers design={design} />
        </Grid>
      </Grid>
    </FolderContextProvider>
  );
};
